import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Form as AntForm, Button, Collapse, FormInstance, Input } from "antd";
import { FC, useCallback, useState } from "react";
import ReactQuill from "react-quill-new";
import styled from "styled-components";
import { ILesson, ILessonChanges, IModule, IModuleChanges } from "utils/types";
// @ts-ignore
import 'react-quill-new/dist/quill.snow.css';
import { FlexBoxHorizontal } from "common/base/flex-box";
import { Text } from "common/base/Text";

const ModuleCollapse = styled(Collapse)`
  margin-bottom: 16px;

  .ant-collapse-header {
    padding: 12px 16px !important;
    font-size: 16px;
    font-weight: 500;
  }

  .ant-collapse-content-box {
    padding: 16px !important;
  }
`;
const LessonCollapse = styled(Collapse)`
  margin-top: 16px;

  .ant-collapse-header {
    padding: 10px 16px !important;
    font-size: 14px;
    font-weight: 500;
  }

  .ant-collapse-content-box {
    padding: 16px !important;
  }
`;
const AddButton = styled(Button)`
  margin-top: 16px;
  color: #5575f7;
  border: 1px solid #5575f7;
  padding: 14px;
  border-radius: 15px;
  box-shadow: none;
  font-family: "Manrope", sans-serif;

  &:hover {
    color: #4563e6;
    background: none;
  }
`;
const { Panel } = Collapse;
const { TextArea } = Input;


export interface CourseContentTabProps {
    onDeleteModule?: (moduleId: string) => Promise<boolean>;
    modules: IModule[];
    formRef: React.RefObject<FormInstance<{modules: IModule[]}>>;
    setModules: React.Dispatch<React.SetStateAction<Partial<IModule>[]>>
    setModuleChanges?: (func: (prev: IModuleChanges) => IModuleChanges) => void;
    setLessonChanges?: (func: (prev: ILessonChanges) => ILessonChanges) => void;
}
export const CourseContentTab:FC<CourseContentTabProps> = ({
    modules, setModules, setModuleChanges, setLessonChanges,
    formRef, onDeleteModule
}) => {
    const [activeModuleKeys, setActiveModuleKeys] = useState<string[]>([modules?.[0]?.id || "module-1"]);
    const [form] = AntForm.useForm();
    const onModuleCollapseChange = (keys: string | string[]) => {
        setActiveModuleKeys(Array.isArray(keys) ? keys : [keys]);
      };
    const onLessonCollapseChange = useCallback((
        moduleIndex: number,
        keys: string | string[]
    ) => {
        const updatedModules = [...modules];
        updatedModules[moduleIndex].activeKey = Array.isArray(keys) ? keys : [keys];
        setModules(structuredClone(updatedModules));
    }, [JSON.stringify(modules)]);
    const handleAddModule = useCallback((moduleId: string) => {
        const newModuleKey = modules.length + 1;
        const newModule: Partial<IModule> = {
            id: "module-" + newModuleKey,
            index: newModuleKey,
            title: "",
            description: "",
            lessons: [{index: 1, id: "lesson-1", title: "", content: "", moduleId}],
            activeKey: ["lesson-1"],
        };
    
        setModules([...modules, newModule]);
        setActiveModuleKeys([...activeModuleKeys, newModuleKey.toString()]);
    }, [JSON.stringify(modules)]);
    const handleAddLesson = useCallback((moduleId: string, moduleIndex: number) => {
        const updatedModules = structuredClone(modules);
        const moduleToUpdate = { ...updatedModules[moduleIndex] };
        const newLessonKey = "lesson-" + String((moduleToUpdate.lessons?.length || 0) + 1);
        moduleToUpdate.lessons = [
          ...(moduleToUpdate.lessons || []),
          {
              id: newLessonKey, index: Number(newLessonKey), title: "", content: "",
              moduleId
          },
        ];
        moduleToUpdate.activeKey = moduleToUpdate.activeKey ? [...moduleToUpdate.activeKey, newLessonKey] : [
            newLessonKey
        ];
        updatedModules[moduleIndex] = moduleToUpdate;
        setModules(updatedModules);
      }, [JSON.stringify(modules)]);
    
    const handleDeleteModule = useCallback((moduleId: string) => {
        if(onDeleteModule){
            onDeleteModule(moduleId);
        }
        setModules([...modules.filter(mod => mod.id !== moduleId)]);
        setActiveModuleKeys([...activeModuleKeys.filter(id => id !== moduleId)]);
    }, [JSON.stringify(modules)]);
    return (
        <AntForm 
            ref={formRef} 
            layout="vertical" 
            form={form} 
            initialValues={{modules}}
            style={{width: "100%"}}
        >
            <ModuleCollapse
                activeKey={activeModuleKeys}
                onChange={onModuleCollapseChange}
            >
            {modules.map((module, moduleIndex) => (
                <Panel 
                    // header={<FlexBoxHorizontal $spaceBtw $align>
                    //     <AppText>Module {moduleIndex + 1}</AppText>
                    // </FlexBoxHorizontal>} 
                    header={`Module ${moduleIndex + 1}`}
                    extra={<AddButton onClick={(e) => {
                        e.stopPropagation(); 
                        if(modules.length === 1){
                            return;
                        }
                        handleDeleteModule(module.id);
                    }}>Delete module</AddButton>}
                    key={module.id}
                >
                    <AntForm.Item
                        name={["modules", moduleIndex, "title"]}
                        label="Title"
                        rules={[
                        { required: true, message: "Please enter a module title" },
                        ]}
                    >
                        <Input placeholder="Enter module title" 
                            size="large"
                            onChange={(e) => {
                                setModuleChanges?.(prev => ({
                                    ...prev,
                                    [module.id]: {
                                        ...(prev[module.id] || {}),
                                        title: e.target.value
                                    }
                                }))
                            }}
                        />
                    </AntForm.Item>

                    <AntForm.Item
                        name={["modules", moduleIndex, "description"]}
                        label="Description"
                    >
                        <TextArea placeholder="Enter module description" rows={4} 
                            onChange={e => {
                                setModuleChanges?.(prev => ({
                                    ...prev,
                                    [module.id]: {
                                        ...prev[module.id],
                                        description: e.target.value
                                    }
                                }))
                            }}
                        />
                    </AntForm.Item>

                <LessonCollapse
                    activeKey={module.activeKey}
                    onChange={(keys) => onLessonCollapseChange(moduleIndex, keys)}
                >
                    {module.lessons?.map((lesson, lessonIndex) => (
                        <Panel header={<FlexBoxHorizontal $spaceBtw>
                            <Text>Lesson {lessonIndex + 1}</Text>
                            {lessonIndex > 0 && <DeleteOutlined style={{color: "red"}} onClick={(e) => {
                                e.stopPropagation();
                                const updatedModules = structuredClone(modules);
                                updatedModules[moduleIndex].lessons = updatedModules[moduleIndex].lessons?.filter((l: ILesson) => l.id !== lesson.id);
                                setModules(updatedModules);
                            }}/>}
                        </FlexBoxHorizontal>} key={lesson.id}>
                            <AntForm.Item
                                name={[
                                    "modules",
                                    moduleIndex,
                                    "lessons",
                                    lessonIndex,
                                    "title",
                                ]}
                                label="Title"
                                rules={[
                                    {
                                        required: true,
                                        message: "Please enter a lesson title",
                                    },
                                ]}
                            >
                                <Input placeholder="Enter lesson title" 
                                    onChange={e => {
                                        setLessonChanges?.(prev => ({
                                            ...(prev || {}), 
                                            [module.id]: {
                                                ...(prev[module.id] || {}),
                                                [lesson.id]: {
                                                    ...(prev[module.id]?.[lesson.id] || {}),
                                                    title: e.target.value,
                                                }
                                            }
                                        }))
                                    }}
                                    size="large"
                                />
                            </AntForm.Item>

                            <AntForm.Item
                                style={{ borderRadius: 20 }}
                                name={[
                                    "modules",
                                    moduleIndex,
                                    "lessons",
                                    lessonIndex,
                                    "content",
                                ]}
                                label="Content"
                            >
                                <ReactQuill theme="snow" value={lesson?.content}  {...{
                                    style: { height: "300px", marginBottom: 20, borderRadius: 20 },
                                    
                                    placeholder: "Enter lesson content",
                                    onChange(value) {
                                        setLessonChanges?.(prev => {
                                            return {
                                                ...prev,
                                                [module.id]: {
                                                    ...(prev[module.id] || {}),
                                                    [lesson.id]: {
                                                        ...(prev[module.id]?.[lesson.id] || {}),
                                                        content: value
                                                    }
                                                }
                                            };
                                        })
                                    },
                                }}/>
                                {/* <TextArea placeholder="Enter lesson content" rows={10} 
                                    onChange={e => {
                                        setLessonChanges?.(prev => {
                                            return {
                                                ...prev,
                                                [module.id]: {
                                                    ...(prev[module.id] || {}),
                                                    [lesson.id]: {
                                                        ...(prev[module.id]?.[lesson.id] || {}),
                                                        content: e.target.value
                                                    }
                                                }
                                            };
                                        })
                                    }}
                                /> */}

                            </AntForm.Item>
                        </Panel>
                    ))}
                </LessonCollapse>

                <AddButton
                    type="link"
                    icon={<PlusOutlined />}
                    onClick={() => handleAddLesson(module.id, moduleIndex)}
                >
                    Add another lesson
                </AddButton>
                </Panel>
            ))}
            </ModuleCollapse>

            <AddButton
                type="link"
                icon={<PlusOutlined />}
                onClick={() => handleAddModule(String(modules.length))}
            >
                Add another module
            </AddButton>
        </AntForm>
    );
}